import { ModuleType } from 'infrastructure/enums';

export const formatNumber = (value: number | string): string =>
  typeof value === 'number'
    ? value.toLocaleString('en-US').replace(/,/g, ' ')
    : value;

export const patientReportColumnLabelMap = {
  activePatients: 'activePatientsReport',
  twoWaySync: '2waySync',
  pendingDischarge: 'patientsPendingDischarge',
  discharged: 'dischargedPatients',
  criticalReadingsOnly: 'croPatients',
  doNotCall: 'doNotCallPatients',
  callingQueue: 'callingQueuePatients',
  onboarding: 'onboardingPatients',
  engaged: 'engagedPatients',
  unengaged: 'unengagedPatients',
  enhancedSupport: 'enhancedSupport',
} as const;

type PatientReportColumnKey = keyof typeof patientReportColumnLabelMap;

// Backend `col` allowlist differs from the response data keys. `active` covers
// both RPM and CCM. `twoWaySync` is not here — it is resolved to
// `twoWaySyncYes` / `twoWaySyncNo` at click time by the card.
export const patientReportColumnExportMap = {
  activePatients: 'active',
  pendingDischarge: 'pendingDischarge',
  discharged: 'discharged',
  criticalReadingsOnly: 'criticalReadingsOnly',
  doNotCall: 'doNotCall',
  callingQueue: 'callingQueue',
  onboarding: 'onboarding',
  engaged: 'engaged',
  unengaged: 'unengaged',
  enhancedSupport: 'enhancedSupport',
} as const satisfies Partial<Record<PatientReportColumnKey, string>>;

export type PatientReportExportCol =
  | (typeof patientReportColumnExportMap)[keyof typeof patientReportColumnExportMap]
  | 'twoWaySyncYes'
  | 'twoWaySyncNo';

// i18n keys under the `patientReportTooltips` namespace. `activePatients` and
// `discharged` differ between RPM and CCM, so they are resolved per module.
const rpmPatientReportTooltipMap = {
  activePatients: 'activePatients',
  pendingDischarge: 'pendingDischarge',
  discharged: 'discharged',
  criticalReadingsOnly: 'criticalReadingsOnly',
  doNotCall: 'doNotCall',
  callingQueue: 'callingQueue',
  onboarding: 'onboarding',
  enhancedSupport: 'enhancedSupport',
} as const satisfies Partial<Record<PatientReportColumnKey, string>>;

const ccmPatientReportTooltipMap = {
  activePatients: 'ccmActivePatients',
  engaged: 'engaged',
  unengaged: 'unengaged',
  discharged: 'ccmDischarged',
} as const satisfies Partial<Record<PatientReportColumnKey, string>>;

type PatientReportTooltipKey =
  | (typeof rpmPatientReportTooltipMap)[keyof typeof rpmPatientReportTooltipMap]
  | (typeof ccmPatientReportTooltipMap)[keyof typeof ccmPatientReportTooltipMap];

export const getPatientReportTooltipKey = (
  key: PatientReportColumnKey,
  isRpm: boolean,
): PatientReportTooltipKey | undefined =>
  (isRpm ? rpmPatientReportTooltipMap : ccmPatientReportTooltipMap)[
    key as never
  ];

export const emptyData = {
  [ModuleType.CCM]: {
    activePatients: { count: 0 },
    engaged: { count: 0 },
    unengaged: { count: 0 },
    discharged: { count: 0 },
  },
  [ModuleType.RPM]: {
    activePatients: { count: 0 },
    twoWaySync: {
      yes: { count: 0, percent: 0 },
      no: { count: 0, percent: 0 },
    },
    pendingDischarge: { count: 0 },
    discharged: { count: 0 },
    criticalReadingsOnly: { count: 0 },
    doNotCall: { count: 0 },
    enhancedSupport: { count: 0 },
    callingQueue: { count: 0 },
    onboarding: { count: 0 },
  },
  [ModuleType.ADMIN]: {},
  [ModuleType.APCM]: {},
  [ModuleType.RPMCCM]: {},
} as const;
